import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { getInfo, updateInfo } from "../../redux/slices/InfoSlice";
import { DispatchType } from "../../interfaces/redux.interface";
import { RootState } from "../../redux/store";
import { startLoading, stopLoading } from "../../redux/slices/WindowSlice";
import { successToast } from "../../utils/toast.util";
import GroupClassFeaturedListItem from "./GroupClassFeatureListItem";
import SubscriptionBenefitsItems from "./SubscriptionBenefitItems";
import { get } from "../../redux/ApiHelper";

const InfoSettings: React.FC = () => {
  const dispatch: DispatchType = useDispatch();
  const { info, updateStatus } = useSelector((state: RootState) => state.info);
  const [availableModels, setAvailableModels] = useState<string[]>([]);

  // Local state for form fields
  const [formValues, setFormValues] = useState({
    teacherSubscription: {
      amount: null,
      validityInDays: null,
    },
    firstTuitionSession: {
      numberOfClasses: null,
      guruCoinCharge: null,
    },
    conversionRate: {
      inrValue: null,
      gcValue: null,
    },
    securityDeposit: {
      perRequestInCoins: null,
      maxCoins: null,
      eligibilityHours: null,
      platformCharge: null,
      gst: null,
    },
    withDraw: {
      maxRequestAmountInCoins: null,
      platformCharge: null,
      gst: null,
    },
    bonas: {
      eligibilityHours: null,
      amountPerBonas: null,
    },
    groupClassPacks: {
      yearlyAmount: null,
      singleAmount: null,
    },
    trialClass: {
      durationInMins: null,
      feesInCoins: null,
    },
    franchiseAdminPack: {
      validity: null,
      amount: null,
    },
    contactAccessPayment: {
      subscribedTeacher: null,
      nonSubscribedTeacher: null,
      subscribedParent: null,
      nonSubscribedParent: null,
    },
    chatAndCallCost: {
      chat: {
        durationInMins: null,
        costInCoins: null,
      },
      call: {
        durationInMins: null,
        costInCoins: null,
      },
    },
    teacherSubjectCertificationSettings: {
      rightAnswerPoints: null,
      wrongAnswerPoints: null,
      passingPercentage: null,
      durationInMinutes: null,
    },
    payoutProtecterCharge: null,
    payoutProtectorAddOneTuitionCancellationCharge: null,
    tuitionCancellation: {
      defaultTuitionCancellationCharge: null,
      gst: null,
    },
    gptModelName: null,
    sponsorTeacherProfile: {
      priceInInr: null,
      durationInDays: null,
      gst: null,
    },
  });

  // State to determine if the button should be disabled
  const [isDisabled, setIsDisabled] = useState(true);

  // Populate form values when info is fetched
  useEffect(() => {
    if (info) {
      setFormValues({ ...formValues, ...info });
    }
    // eslint-disable-next-line
  }, [info]);

  // Check if button should be disabled
  useEffect(() => {
    const isFormUnchanged =
      info && JSON.stringify(formValues) === JSON.stringify(info);
    const hasNullValues = Object.values(formValues.teacherSubscription).some(
      (value) => value === null || value === ""
    );
    const hasFirstTuitionNullValues = Object.values(
      formValues.firstTuitionSession
    ).some((value) => value === null || value === "");
    const hasCertificationNullValues = Object.values(
      formValues.teacherSubjectCertificationSettings
    ).some((value) => value === null || value === "");
    const hasPayoutAndCancellationNullValues = [
      formValues.payoutProtecterCharge,
      formValues.payoutProtectorAddOneTuitionCancellationCharge,
      formValues.tuitionCancellation?.defaultTuitionCancellationCharge,
      formValues.tuitionCancellation?.gst,
    ].some((value) => value === null || value === "");
    const hasAiModelNullValue =
      formValues.gptModelName === null || formValues.gptModelName === "";
    const hasSponsorTeacherProfileNullValues = Object.values(
      formValues.sponsorTeacherProfile ?? {}
    ).some((value) => value === null || value === "");
    setIsDisabled(
      isFormUnchanged ||
        hasNullValues ||
        hasFirstTuitionNullValues ||
        hasCertificationNullValues ||
        hasPayoutAndCancellationNullValues ||
        hasAiModelNullValue ||
        hasSponsorTeacherProfileNullValues
    );
  }, [formValues, info]);

  // Fetch info on component mount
  useEffect(() => {
    if (!info) {
      dispatch(startLoading());
      dispatch(getInfo()).finally(() => {
        dispatch(stopLoading());
      });
    }

    //eslint-disable-next-line
  }, [info]);

  useEffect(() => {
    (async () => {
      try {
        const res = await get("/info/openai-models");
        const models = Array.isArray(res?.models) ? res.models : [];
        setAvailableModels(models);
      } catch (e) {
        setAvailableModels([]);
      }
    })();
  }, []);

  // Handle input changes
  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement>,
    section:
      | "teacherSubscription"
      | "firstTuitionSession"
      | "conversionRate"
      | "securityDeposit"
      | "withDraw"
      | "bonas"
      | "groupClassPacks"
      | "trialClass"
      | "franchiseAdminPack"
      | "contactAccessPayment"
      | "chatAndCallCost"
      | "teacherSubjectCertificationSettings"
      | "sponsorTeacherProfile",

    field: string,
    subField?: string
  ) => {
    if (subField && section === "chatAndCallCost") {
      setFormValues((prev) => ({
        ...prev,
        [section]: {
          ...prev[section],
          [field]: {
            ...prev[section][field],
            [subField]: e.target.value ?? "",
          },
        },
      }));
    } else {
      setFormValues((prev) => ({
        ...prev,
        [section]: {
          ...prev[section],
          [field]: e.target.value ?? "", // Safely handle null or undefined
        },
      }));
    }
  };

  const handleRootChange = (
    e: React.ChangeEvent<HTMLInputElement>,
    field:
      | "payoutProtecterCharge"
      | "payoutProtectorAddOneTuitionCancellationCharge"
  ) => {
    setFormValues((prev) => ({
      ...prev,
      [field]: e.target.value ?? "",
    }));
  };

  const handleTuitionCancellationChange = (
    e: React.ChangeEvent<HTMLInputElement>,
    field: "defaultTuitionCancellationCharge" | "gst"
  ) => {
    setFormValues((prev) => ({
      ...prev,
      tuitionCancellation: {
        ...prev.tuitionCancellation,
        [field]: e.target.value ?? "",
      },
    }));
  };

  const handleRootSelectChange = (
    e: React.ChangeEvent<HTMLSelectElement>,
    field: "gptModelName"
  ) => {
    setFormValues((prev) => ({
      ...prev,
      [field]: e.target.value ?? "",
    }));
  };

  // Handle form submission
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    dispatch(startLoading());
    dispatch(updateInfo(formValues))
      .unwrap()
      .then(() => {
        successToast("Settings updated successfully");
      })
      .finally(() => {
        dispatch(stopLoading());
      });
  };

  return (
    <div className="bg-gray-900 text-yellow-500 flex items-center justify-center p-4 flex-grow">
      <div className="bg-gray-800 p-6 rounded-lg shadow-lg w-full max-w-2xl overflow-auto">
        <h1 className="text-xl font-semibold mb-4">Update Info Settings</h1>
        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Teacher Subscription Section */}
          <div>
            <h2 className="text-lg font-semibold mb-2">Teacher Subscription</h2>
            <label className="block mb-2 text-left">
              Amount:
              <input
                type="number"
                value={formValues.teacherSubscription.amount ?? ""}
                onChange={(e) =>
                  handleChange(e, "teacherSubscription", "amount")
                }
                className="w-full p-2 rounded bg-gray-700 text-yellow-500 focus:outline-none focus:ring-2 focus:ring-yellow-500"
              />
            </label>
            <label className="block text-left">
              Validity (in days):
              <input
                type="number"
                value={formValues.teacherSubscription.validityInDays ?? ""}
                onChange={(e) =>
                  handleChange(e, "teacherSubscription", "validityInDays")
                }
                className="w-full p-2 rounded bg-gray-700 text-yellow-500 focus:outline-none focus:ring-2 focus:ring-yellow-500"
              />
            </label>
          </div>
          <SubscriptionBenefitsItems />

          {/* First Tuition Section */}
          <div>
            <h2 className="text-lg font-semibold mb-2">
              First Tuition Session Settings
            </h2>
            <p className="text-sm text-gray-400">
              Configure initial tuition settings for a student-teacher pairing.
              For the first enrollment, the admin sets the number of classes and
              tuition fee. These settings apply only to the initial session. If
              the student continues with the same teacher, different settings
              will apply. These details will reflect on the tuition request page
              for the admin to review before approving or rejecting the request.
            </p>

            <label className="block mb-2 text-left">
              Number of Classes:
              <input
                type="number"
                value={formValues.firstTuitionSession.numberOfClasses ?? ""}
                onChange={(e) =>
                  handleChange(e, "firstTuitionSession", "numberOfClasses")
                }
                className="w-full p-2 rounded bg-gray-700 text-yellow-500 focus:outline-none focus:ring-2 focus:ring-yellow-500"
              />
            </label>
            <label className="block text-left">
              Guru Coin Charge:
              <input
                type="number"
                value={formValues.firstTuitionSession.guruCoinCharge ?? ""}
                onChange={(e) =>
                  handleChange(e, "firstTuitionSession", "guruCoinCharge")
                }
                className="w-full p-2 rounded bg-gray-700 text-yellow-500 focus:outline-none focus:ring-2 focus:ring-yellow-500"
              />
            </label>
          </div>

          {/* Conversion Rate Section */}
          <div>
            <h2 className="text-lg font-semibold mb-2">Conversion Rate</h2>
            <p className="text-sm text-gray-400">
              Note: These settings will reflect during the Guru Coin package
              creation.
            </p>
            <label className="block mb-2 text-left">
              INR Value:
              <input
                type="number"
                value={formValues.conversionRate.inrValue ?? ""}
                onChange={(e) => handleChange(e, "conversionRate", "inrValue")}
                className="w-full p-2 rounded bg-gray-700 text-yellow-500 focus:outline-none focus:ring-2 focus:ring-yellow-500"
              />
            </label>
            <label className="block text-left">
              Guru Coin Value:
              <input
                type="number"
                value={formValues.conversionRate.gcValue ?? ""}
                onChange={(e) => handleChange(e, "conversionRate", "gcValue")}
                className="w-full p-2 rounded bg-gray-700 text-yellow-500 focus:outline-none focus:ring-2 focus:ring-yellow-500"
              />
            </label>
          </div>

          <div>
            <h2 className="text-lg font-semibold mb-2">
              Security Deposit Limit settings
            </h2>
            <p className="text-sm text-gray-400">
              Note: These settings will reflect during the payout request
              creation.
            </p>
            <label className="block mb-2 text-left">
              No of coins add as Security deposit per withdraw request:
              <input
                type="number"
                value={formValues.securityDeposit.perRequestInCoins ?? ""}
                onChange={(e) =>
                  handleChange(e, "securityDeposit", "perRequestInCoins")
                }
                className="w-full p-2 rounded bg-gray-700 text-yellow-500 focus:outline-none focus:ring-2 focus:ring-yellow-500"
              />
            </label>
            <label className="block text-left">
              Total security coins collect per year for a perticular student:
              <input
                type="number"
                value={formValues.securityDeposit.maxCoins ?? ""}
                onChange={(e) => handleChange(e, "securityDeposit", "maxCoins")}
                className="w-full p-2 rounded bg-gray-700 text-yellow-500 focus:outline-none focus:ring-2 focus:ring-yellow-500"
              />
            </label>
            <label className="block text-left">
              Min total hours per year for withdraw eligibility:
              <input
                type="number"
                value={formValues.securityDeposit.eligibilityHours ?? ""}
                onChange={(e) =>
                  handleChange(e, "securityDeposit", "eligibilityHours")
                }
                className="w-full p-2 rounded bg-gray-700 text-yellow-500 focus:outline-none focus:ring-2 focus:ring-yellow-500"
              />
            </label>
          </div>
          <div>
            <h2 className="text-lg font-semibold mb-2">
              Security Deposit Withdraw (in February)
            </h2>
            <p className="text-sm text-gray-400">
              Note: These settings will be applied when creating a security
              deposit withdrawal request.
            </p>
            <label className="block mb-2 text-left">
              Platform charge(%):
              <input
                type="number"
                value={formValues.securityDeposit.platformCharge ?? ""}
                onChange={(e) =>
                  handleChange(e, "securityDeposit", "platformCharge")
                }
                className="w-full p-2 rounded bg-gray-700 text-yellow-500 focus:outline-none focus:ring-2 focus:ring-yellow-500"
              />
            </label>
            <label className="block mb-2 text-left">
              GST(%):
              <input
                type="number"
                value={formValues.securityDeposit.gst ?? ""}
                onChange={(e) => handleChange(e, "securityDeposit", "gst")}
                className="w-full p-2 rounded bg-gray-700 text-yellow-500 focus:outline-none focus:ring-2 focus:ring-yellow-500"
              />
            </label>
          </div>

          <div>
            <h2 className="text-lg font-semibold mb-2">General Withdraw</h2>
            <p className="text-sm text-gray-400">
              Note: These settings will reflect during the payout request
              creation.
            </p>
            <label className="block mb-2 text-left">
              No of coins per withdraw request for a teacher:
              <input
                type="number"
                value={formValues.withDraw.maxRequestAmountInCoins ?? ""}
                onChange={(e) =>
                  handleChange(e, "withDraw", "maxRequestAmountInCoins")
                }
                className="w-full p-2 rounded bg-gray-700 text-yellow-500 focus:outline-none focus:ring-2 focus:ring-yellow-500"
              />
            </label>
            <label className="block mb-2 text-left">
              Platform charge(%):
              <input
                type="number"
                value={formValues.withDraw.platformCharge ?? ""}
                onChange={(e) => handleChange(e, "withDraw", "platformCharge")}
                className="w-full p-2 rounded bg-gray-700 text-yellow-500 focus:outline-none focus:ring-2 focus:ring-yellow-500"
              />
            </label>
            <label className="block mb-2 text-left">
              GST(%):
              <input
                type="number"
                value={formValues.withDraw.gst ?? ""}
                onChange={(e) => handleChange(e, "withDraw", "gst")}
                className="w-full p-2 rounded bg-gray-700 text-yellow-500 focus:outline-none focus:ring-2 focus:ring-yellow-500"
              />
            </label>
          </div>

          <div>
            <h2 className="text-lg font-semibold mb-2">
              Payout Protector & Tuition Cancellation Charges
            </h2>
            <p className="text-sm text-gray-400">
              Note: These settings will apply during payout protection and tuition cancellation.
            </p>
            <label className="block mb-2 text-left">
              Payout Protector Charge (INR):
              <input
                type="number"
                value={formValues.payoutProtecterCharge ?? ""}
                onChange={(e) => handleRootChange(e, "payoutProtecterCharge")}
                className="w-full p-2 rounded bg-gray-700 text-yellow-500 focus:outline-none focus:ring-2 focus:ring-yellow-500"
              />
            </label>
            <label className="block mb-2 text-left">
              Payout Protector Add-on Tuition Cancellation Charge (INR):
              <input
                type="number"
                value={
                  formValues.payoutProtectorAddOneTuitionCancellationCharge ?? ""
                }
                onChange={(e) =>
                  handleRootChange(
                    e,
                    "payoutProtectorAddOneTuitionCancellationCharge"
                  )
                }
                className="w-full p-2 rounded bg-gray-700 text-yellow-500 focus:outline-none focus:ring-2 focus:ring-yellow-500"
              />
            </label>
            <label className="block mb-2 text-left">
              Default Tuition Cancellation Charge (INR):
              <input
                type="number"
                value={
                  formValues.tuitionCancellation?.defaultTuitionCancellationCharge ??
                  ""
                }
                onChange={(e) =>
                  handleTuitionCancellationChange(
                    e,
                    "defaultTuitionCancellationCharge"
                  )
                }
                className="w-full p-2 rounded bg-gray-700 text-yellow-500 focus:outline-none focus:ring-2 focus:ring-yellow-500"
              />
            </label>
            <label className="block text-left">
              GST on Tuition Cancellation (%):
              <input
                type="number"
                value={formValues.tuitionCancellation?.gst ?? ""}
                onChange={(e) => handleTuitionCancellationChange(e, "gst")}
                className="w-full p-2 rounded bg-gray-700 text-yellow-500 focus:outline-none focus:ring-2 focus:ring-yellow-500"
              />
            </label>
          </div>
          <div>
            <h2 className="text-lg font-semibold mb-2">Bonus Settings</h2>
            <p className="text-sm text-gray-400">
              Note: These settings will reflect during assigning the bonus
              amount.
            </p>
            <label className="block mb-2 text-left">
              Eligiblty Hours:
              <input
                type="number"
                value={formValues.bonas.eligibilityHours ?? ""}
                onChange={(e) => handleChange(e, "bonas", "eligibilityHours")}
                className="w-full p-2 rounded bg-gray-700 text-yellow-500 focus:outline-none focus:ring-2 focus:ring-yellow-500"
              />
            </label>
            <label className="block mb-2 text-left">
              Assign bonus about (in coins) when each eligiblity hours
              completed:
              <input
                type="number"
                value={formValues.bonas.amountPerBonas ?? ""}
                onChange={(e) => handleChange(e, "bonas", "amountPerBonas")}
                className="w-full p-2 rounded bg-gray-700 text-yellow-500 focus:outline-none focus:ring-2 focus:ring-yellow-500"
              />
            </label>
          </div>

          <div>
            <h2 className="text-lg font-semibold mb-2">
              Contact Access payment Settings
            </h2>
            <p className="text-sm text-gray-400">
              Note: These settings will reflect during contact access payment.
            </p>
            <label className="block mb-2 text-left">
              For subscribed parent:
              <input
                type="number"
                value={formValues.contactAccessPayment.subscribedParent ?? ""}
                onChange={(e) =>
                  handleChange(e, "contactAccessPayment", "subscribedParent")
                }
                className="w-full p-2 rounded bg-gray-700 text-yellow-500 focus:outline-none focus:ring-2 focus:ring-yellow-500"
              />
            </label>
            <label className="block mb-2 text-left">
              For non subscribed parent:
              <input
                type="number"
                value={
                  formValues.contactAccessPayment.nonSubscribedParent ?? ""
                }
                onChange={(e) =>
                  handleChange(e, "contactAccessPayment", "nonSubscribedParent")
                }
                className="w-full p-2 rounded bg-gray-700 text-yellow-500 focus:outline-none focus:ring-2 focus:ring-yellow-500"
              />
            </label>
            <label className="block mb-2 text-left">
              For subscribed teacher:
              <input
                type="number"
                value={formValues.contactAccessPayment.subscribedTeacher ?? ""}
                onChange={(e) =>
                  handleChange(e, "contactAccessPayment", "subscribedTeacher")
                }
                className="w-full p-2 rounded bg-gray-700 text-yellow-500 focus:outline-none focus:ring-2 focus:ring-yellow-500"
              />
            </label>
            <label className="block mb-2 text-left">
              For non subscribed teacher:
              <input
                type="number"
                value={
                  formValues.contactAccessPayment.nonSubscribedTeacher ?? ""
                }
                onChange={(e) =>
                  handleChange(
                    e,
                    "contactAccessPayment",
                    "nonSubscribedTeacher"
                  )
                }
                className="w-full p-2 rounded bg-gray-700 text-yellow-500 focus:outline-none focus:ring-2 focus:ring-yellow-500"
              />
            </label>
          </div>

          <div>
            <h2 className="text-lg font-semibold mb-2">
              Group class pack Settings
            </h2>
            <p className="text-sm text-gray-400">
              Note: These settings will apply when accessing the contact details
              of a parent group class application..
            </p>
            <label className="block mb-2 text-left">
              Yearly amount (in INR):
              <input
                type="number"
                value={formValues.groupClassPacks.yearlyAmount ?? ""}
                onChange={(e) =>
                  handleChange(e, "groupClassPacks", "yearlyAmount")
                }
                className="w-full p-2 rounded bg-gray-700 text-yellow-500 focus:outline-none focus:ring-2 focus:ring-yellow-500"
              />
            </label>
            <label className="block mb-2 text-left">
              Single amount (in INR):
              <input
                type="number"
                value={formValues.groupClassPacks.singleAmount ?? ""}
                onChange={(e) =>
                  handleChange(e, "groupClassPacks", "singleAmount")
                }
                className="w-full p-2 rounded bg-gray-700 text-yellow-500 focus:outline-none focus:ring-2 focus:ring-yellow-500"
              />
            </label>
          </div>

          <div>
            <h2 className="text-lg font-semibold mb-2">Trial class Settings</h2>
            <p className="text-sm text-gray-400">
              Note: These settings will apply when requesting trial class.
            </p>
            <label className="block mb-2 text-left">
              Trial class duration (in Mins):
              <input
                type="number"
                value={formValues.trialClass.durationInMins ?? ""}
                onChange={(e) =>
                  handleChange(e, "trialClass", "durationInMins")
                }
                className="w-full p-2 rounded bg-gray-700 text-yellow-500 focus:outline-none focus:ring-2 focus:ring-yellow-500"
              />
            </label>
            <label className="block mb-2 text-left">
              Fees per trial class (in Coins):
              <input
                type="number"
                value={formValues.trialClass.feesInCoins ?? ""}
                onChange={(e) => handleChange(e, "trialClass", "feesInCoins")}
                className="w-full p-2 rounded bg-gray-700 text-yellow-500 focus:outline-none focus:ring-2 focus:ring-yellow-500"
              />
            </label>
          </div>
          <div>
            <h2 className="text-lg font-semibold mb-2">
              Franchise admin package settings
            </h2>
            <p className="text-sm text-gray-400">
              Note: These settings will apply when franchise admin pay.
            </p>
            <label className="block mb-2 text-left">
              Access duration (in days):
              <input
                type="number"
                value={formValues.franchiseAdminPack.validity ?? ""}
                onChange={(e) =>
                  handleChange(e, "franchiseAdminPack", "validity")
                }
                className="w-full p-2 rounded bg-gray-700 text-yellow-500 focus:outline-none focus:ring-2 focus:ring-yellow-500"
              />
            </label>
            <label className="block mb-2 text-left">
              Amount (in inr):
              <input
                type="number"
                value={formValues.franchiseAdminPack.amount ?? ""}
                onChange={(e) =>
                  handleChange(e, "franchiseAdminPack", "amount")
                }
                className="w-full p-2 rounded bg-gray-700 text-yellow-500 focus:outline-none focus:ring-2 focus:ring-yellow-500"
              />
            </label>
          </div>

          <div>
            <h2 className="text-lg font-semibold mb-2">
              Chat and Call Cost Settings
            </h2>
            <p className="text-sm text-gray-400">
              Note: These settings will apply when users access chat and call
              features.
            </p>
            <div className="mb-4">
              <h3 className="text-md font-medium mb-2">Chat Settings</h3>
              <label className="block mb-2 text-left">
                Duration (in minutes):
                <input
                  type="number"
                  value={formValues.chatAndCallCost.chat.durationInMins ?? ""}
                  onChange={(e) =>
                    handleChange(e, "chatAndCallCost", "chat", "durationInMins")
                  }
                  className="w-full p-2 rounded bg-gray-700 text-yellow-500 focus:outline-none focus:ring-2 focus:ring-yellow-500"
                />
              </label>
              <label className="block mb-2 text-left">
                Cost (in coins):
                <input
                  type="number"
                  value={formValues.chatAndCallCost.chat.costInCoins ?? ""}
                  onChange={(e) =>
                    handleChange(e, "chatAndCallCost", "chat", "costInCoins")
                  }
                  className="w-full p-2 rounded bg-gray-700 text-yellow-500 focus:outline-none focus:ring-2 focus:ring-yellow-500"
                />
              </label>
            </div>
            <div>
              <h3 className="text-md font-medium mb-2">Call Settings</h3>
              <label className="block mb-2 text-left">
                Duration (in minutes):
                <input
                  type="number"
                  value={formValues.chatAndCallCost.call.durationInMins ?? ""}
                  onChange={(e) =>
                    handleChange(e, "chatAndCallCost", "call", "durationInMins")
                  }
                  className="w-full p-2 rounded bg-gray-700 text-yellow-500 focus:outline-none focus:ring-2 focus:ring-yellow-500"
                />
              </label>
              <label className="block mb-2 text-left">
                Cost (in coins):
                <input
                  type="number"
                  value={formValues.chatAndCallCost.call.costInCoins ?? ""}
                  onChange={(e) =>
                    handleChange(e, "chatAndCallCost", "call", "costInCoins")
                  }
                  className="w-full p-2 rounded bg-gray-700 text-yellow-500 focus:outline-none focus:ring-2 focus:ring-yellow-500"
                />
              </label>
            </div>
          </div>

          <div>
            <h2 className="text-lg font-semibold mb-2">
              Teacher Subject Certification Settings
            </h2>
            <p className="text-sm text-gray-400">
              Note: These settings will apply when teachers take subject certification exams.
            </p>
            <label className="block mb-2 text-left">
              Right Answer Points:
              <input
                type="number"
                value={formValues.teacherSubjectCertificationSettings.rightAnswerPoints ?? ""}
                onChange={(e) =>
                  handleChange(e, "teacherSubjectCertificationSettings", "rightAnswerPoints")
                }
                className="w-full p-2 rounded bg-gray-700 text-yellow-500 focus:outline-none focus:ring-2 focus:ring-yellow-500"
              />
            </label>
            <label className="block mb-2 text-left">
              Wrong Answer Points (must be negative):
              <input
                type="number"
                step="any"
                value={formValues.teacherSubjectCertificationSettings.wrongAnswerPoints ?? ""}
                onChange={(e) => {
                  const value = e.target.value;
                  // Allow negative numbers and empty string
                  if (value === "" || value === "-" || (!isNaN(Number(value)) && Number(value) <= 0)) {
                    handleChange(e, "teacherSubjectCertificationSettings", "wrongAnswerPoints");
                  }
                }}
                onBlur={(e) => {
                  // Ensure it's negative on blur
                  const value = Number(e.target.value);
                  if (e.target.value !== "" && value > 0) {
                    e.target.value = String(-value);
                    const syntheticEvent = {
                      ...e,
                      target: { ...e.target, value: String(-value) }
                    } as React.ChangeEvent<HTMLInputElement>;
                    handleChange(syntheticEvent, "teacherSubjectCertificationSettings", "wrongAnswerPoints");
                  }
                }}
                className="w-full p-2 rounded bg-gray-700 text-yellow-500 focus:outline-none focus:ring-2 focus:ring-yellow-500"
              />
            </label>
            <label className="block mb-2 text-left">
              Passing Percentage:
              <input
                type="number"
                value={formValues.teacherSubjectCertificationSettings.passingPercentage ?? ""}
                onChange={(e) =>
                  handleChange(e, "teacherSubjectCertificationSettings", "passingPercentage")
                }
                className="w-full p-2 rounded bg-gray-700 text-yellow-500 focus:outline-none focus:ring-2 focus:ring-yellow-500"
              />
            </label>
            <label className="block text-left">
              Exam Duration (in minutes):
              <input
                type="number"
                value={formValues.teacherSubjectCertificationSettings.durationInMinutes ?? ""}
                onChange={(e) =>
                  handleChange(
                    e,
                    "teacherSubjectCertificationSettings",
                    "durationInMinutes"
                  )
                }
                className="w-full p-2 rounded bg-gray-700 text-yellow-500 focus:outline-none focus:ring-2 focus:ring-yellow-500"
              />
            </label>
          </div>

          <div>
            <h2 className="text-lg font-semibold mb-2">AI Model Settings</h2>
            <p className="text-sm text-gray-400">
              Note: This model will be used for AI-generated certification content.
            </p>
            <label className="block text-left">
              GPT Model Name:
              <select
                value={formValues.gptModelName ?? ""}
                onChange={(e) => handleRootSelectChange(e, "gptModelName")}
                className="w-full p-2 rounded bg-gray-700 text-yellow-500 focus:outline-none focus:ring-2 focus:ring-yellow-500"
              >
                <option value="" disabled>
                  Select a model
                </option>
                {availableModels.length > 0 ? (
                  availableModels.map((m) => (
                    <option key={m} value={m}>
                      {m}
                    </option>
                  ))
                ) : (
                  <>
                    <option value="gpt-4o-mini">gpt-4o-mini</option>
                    <option value="gpt-4o">gpt-4o</option>
                    <option value="gpt-4-turbo">gpt-4-turbo</option>
                  </>
                )}
              </select>
            </label>
          </div>

          <div>
            <h2 className="text-lg font-semibold mb-2">
              Sponsor Teacher Profile Settings
            </h2>
            <p className="text-sm text-gray-400">
              Note: These settings will apply when a teacher purchases a
              sponsored profile placement.
            </p>
            <label className="block mb-2 text-left">
              Price (in INR):
              <input
                type="number"
                value={formValues.sponsorTeacherProfile?.priceInInr ?? ""}
                onChange={(e) =>
                  handleChange(e, "sponsorTeacherProfile", "priceInInr")
                }
                className="w-full p-2 rounded bg-gray-700 text-yellow-500 focus:outline-none focus:ring-2 focus:ring-yellow-500"
              />
            </label>
            <label className="block mb-2 text-left">
              Duration (in days):
              <input
                type="number"
                value={formValues.sponsorTeacherProfile?.durationInDays ?? ""}
                onChange={(e) =>
                  handleChange(e, "sponsorTeacherProfile", "durationInDays")
                }
                className="w-full p-2 rounded bg-gray-700 text-yellow-500 focus:outline-none focus:ring-2 focus:ring-yellow-500"
              />
            </label>
            <label className="block text-left">
              GST (%):
              <input
                type="number"
                value={formValues.sponsorTeacherProfile?.gst ?? ""}
                onChange={(e) =>
                  handleChange(e, "sponsorTeacherProfile", "gst")
                }
                className="w-full p-2 rounded bg-gray-700 text-yellow-500 focus:outline-none focus:ring-2 focus:ring-yellow-500"
              />
            </label>
          </div>
          {/* Submit Button */}
          <button
            type="submit"
            className={`w-full py-2 px-4 rounded focus:outline-none focus:ring-2 ${isDisabled
              ? "bg-yellow-500 opacity-50 hover:bg-yellow-500 text-gray-300 cursor-not-allowed"
              : "bg-yellow-500 text-gray-900 hover:bg-yellow-400 focus:ring-yellow-500"
              }`}
            disabled={isDisabled}
          >
            {updateStatus === "loading" ? "Updating..." : "Update Settings"}
          </button>
        </form>
        <GroupClassFeaturedListItem />
      </div>
    </div>
  );
};

export default InfoSettings;
